/** Runtime validation for requests accepted by the native workspace bridge. */

type FieldKind =
  | 'string'
  | 'number'
  | 'boolean'
  | 'string[]'
  | 'string-record'
  | 'record'
  | 'nullable-string'
  | 'nullable-number'
  | 'nullable-record'
  | 'unknown';

interface FieldSpec {
  kind: FieldKind;
  optional?: boolean;
}

const required = (kind: FieldKind): FieldSpec => ({ kind });
const optional = (kind: FieldKind): FieldSpec => ({ kind, optional: true });

const commonWorkspace = { workspace_root: optional('string') } satisfies Record<string, FieldSpec>;
const commonRequest = { request_id: optional('string') } satisfies Record<string, FieldSpec>;

const requestSchemas = {
  workspace_checkout: {
    target: required('string'),
    // The project's repository on Crowdex Cloud, and the header that authorizes this sandbox.
    remote_url: required('string'),
    authorization: optional('nullable-string'),
    // The forge the project comes from, kept as `origin` (its branches, pull requests).
    origin_url: optional('nullable-string'),
    base_commit_sha: required('string'),
    patch: optional('nullable-string'),
  },
  workspace_snapshot: { ...commonWorkspace, base_commit_sha: required('string') },
  workspace_archive: { ...commonWorkspace, ...commonRequest },
  workspace_restore_archive: {
    ...commonWorkspace,
    ...commonRequest,
    content: required('string'),
  },
  workspace_read_text_file: {
    ...commonWorkspace,
    ...commonRequest,
    path: required('string'),
    line: optional('nullable-number'),
    limit: optional('nullable-number'),
  },
  workspace_read_binary_file: { ...commonWorkspace, ...commonRequest, path: required('string') },
  workspace_read_file_chunk: {
    ...commonWorkspace,
    ...commonRequest,
    path: required('string'),
    offset: required('number'),
    length: required('number'),
  },
  workspace_write_text_file: {
    ...commonWorkspace,
    ...commonRequest,
    path: required('string'),
    content: required('string'),
  },
  workspace_write_binary_file: {
    ...commonWorkspace,
    ...commonRequest,
    path: required('string'),
    content: required('string'),
  },
  workspace_copy_file: {
    ...commonWorkspace,
    ...commonRequest,
    source: required('string'),
    target: required('string'),
  },
  workspace_remove_file: { ...commonWorkspace, ...commonRequest, path: required('string') },
  workspace_list_directory: {
    ...commonWorkspace,
    ...commonRequest,
    relative_path: optional('string'),
  },
  // A turn's checkpoint (`metadata.checkpoint` of its message): its diff, without the files
  // another conversation wrote (`exclude`).
  workspace_checkpoint_diff: {
    ...commonWorkspace,
    ...commonRequest,
    before: required('string'),
    after: required('string'),
    path: optional('nullable-string'),
    exclude: optional('string[]'),
  },
  // Undoing a turn, or restoring the files of the turns an edited message drops: `turns` are
  // `CheckpointRevertTurn`s, oldest first; `dry_run` only lists the files and their conflicts.
  workspace_checkpoint_revert: {
    ...commonWorkspace,
    ...commonRequest,
    turns: required('unknown'),
    overwrite: optional('string[]'),
    keep: optional('string[]'),
    dry_run: optional('boolean'),
  },
  workspace_git_review: { ...commonWorkspace, ...commonRequest },
  workspace_git_stage: { ...commonWorkspace, ...commonRequest, path: required('string') },
  workspace_git_unstage: { ...commonWorkspace, ...commonRequest, path: required('string') },
  workspace_git_revert: { ...commonWorkspace, ...commonRequest, path: required('string') },
  workspace_git_status: { ...commonWorkspace, ...commonRequest },
  workspace_git_watch: { workspace_root: required('string'), ...commonRequest },
  workspace_git_unwatch: { workspace_root: required('string'), ...commonRequest },
  // `paths`: a conversation's files, the scope of the commit; absent, the whole folder.
  workspace_git_generation_context: {
    ...commonWorkspace,
    ...commonRequest,
    paths: optional('string[]'),
  },
  workspace_git_commit: {
    ...commonWorkspace,
    ...commonRequest,
    message: required('string'),
    push: optional('boolean'),
    paths: optional('string[]'),
  },
  workspace_git_push: { ...commonWorkspace, ...commonRequest },
  workspace_git_snapshot: { ...commonWorkspace, ...commonRequest, base_ref: required('string') },
  // Server -> CLI: push commits of the folder to its cloud project's repository (`<sha>:<ref>`).
  workspace_cloud_push: {
    ...commonWorkspace,
    ...commonRequest,
    remote_url: required('string'),
    authorization: required('string'),
    refspecs: required('string[]'),
  },
  // Server -> CLI: continue a Cloud conversation in a managed worktree (or, for a
  // folder without Git, a managed directory) without modifying `workspace_root`.
  workspace_handoff_return: {
    workspace_root: required('string'),
    ...commonRequest,
    owner_thread_id: required('string'),
    base_ref: required('string'),
    base_commit_sha: required('string'),
    patch: required('string'),
    archive: optional('nullable-string'),
    // The cloud project's repository, where a base this machine lacks is fetched.
    remote_url: optional('nullable-string'),
    authorization: optional('nullable-string'),
    branch: optional('nullable-string'),
    worktree_root: optional('nullable-string'),
  },
  workspace_git_switch_branch: {
    ...commonWorkspace,
    ...commonRequest,
    branch: required('string'),
    stash: optional('boolean'),
    remote: optional('boolean'),
    diverged: optional('nullable-string'),
  },
  workspace_git_fetch: { ...commonWorkspace, ...commonRequest },
  workspace_git_create_branch: {
    ...commonWorkspace,
    ...commonRequest,
    branch: required('string'),
  },
  workspace_git_clone: {
    ...commonRequest,
    destination_root: optional('string'),
    clone_url: required('string'),
  },
  workspace_pull_request_cockpit: { ...commonWorkspace, ...commonRequest },
  workspace_git_create_pull_request: {
    ...commonWorkspace,
    ...commonRequest,
    title: required('string'),
    body: required('string'),
    draft: optional('boolean'),
  },
  workspace_git_merge_pull_request: {
    ...commonWorkspace,
    ...commonRequest,
    method: optional('string'),
    expected_head_sha: optional('string'),
  },
  workspace_git_mutate_pull_request: {
    ...commonWorkspace,
    ...commonRequest,
    action: required('string'),
    expected_head_sha: required('string'),
    title: optional('string'),
    body: optional('string'),
    decision: optional('string'),
    reviewers: optional('string[]'),
    method: optional('string'),
    thread_id: optional('string'),
    comment_id: optional('string'),
    reaction: optional('string'),
  },
  workspace_environment_get: { ...commonWorkspace, ...commonRequest },
  workspace_list_ports: { ...commonRequest },
  workspace_environment_set: {
    ...commonWorkspace,
    ...commonRequest,
    // `null` persists an explicit opt-out of the repo-provided environment.
    environment: required('nullable-record'),
  },
  // Desktop sends `null` for an unset starting branch, environment and worktree root.
  workspace_git_create_managed_worktree: {
    ...commonWorkspace,
    ...commonRequest,
    owner_thread_id: optional('string'),
    starting_branch: optional('nullable-string'),
    starting_remote: optional('boolean'),
    environment: optional('nullable-record'),
    worktree_root: optional('nullable-string'),
  },
  // A conversation's worktree captured as one commit on its starting point.
  workspace_git_capture_worktree: {
    ...commonWorkspace,
    ...commonRequest,
    owner_thread_id: required('string'),
    message: optional('nullable-string'),
    worktree_root: optional('nullable-string'),
  },
  // Commits brought one after another into an integration worktree of the repository.
  workspace_git_integrate: {
    ...commonWorkspace,
    ...commonRequest,
    name: required('string'),
    commits: required('string[]'),
    base: optional('nullable-string'),
    worktree_root: optional('nullable-string'),
  },
  workspace_git_handoff_worktree: {
    ...commonWorkspace,
    ...commonRequest,
    worktree_path: required('string'),
    destination: optional('string'),
  },
  workspace_git_restore_worktree: {
    ...commonRequest,
    worktree_path: required('string'),
  },
  workspace_git_delete_worktree: {
    ...commonWorkspace,
    ...commonRequest,
    worktree_path: required('string'),
  },
  workspace_git_cleanup_worktrees: {
    ...commonWorkspace,
    ...commonRequest,
    worktree_root: optional('nullable-string'),
    protected_owner_ids: optional('string[]'),
    keep_count: optional('number'),
  },
  workspace_run_command: {
    ...commonWorkspace,
    ...commonRequest,
    relative_path: optional('string'),
    command: required('string'),
  },
  acp_discover_agents: { ...commonRequest },
  acp_agent_catalog: { ...commonRequest },
  agent_terminal_create: {
    ...commonWorkspace,
    ...commonRequest,
    terminal_id: required('string'),
    output_byte_limit: optional('number'),
    cwd: optional('string'),
    command: required('string'),
    args: optional('string[]'),
    env: optional('string-record'),
  },
  agent_terminal_output: { ...commonRequest, terminal_id: required('string') },
  agent_terminal_wait: { ...commonRequest, terminal_id: required('string') },
  agent_terminal_kill: { ...commonRequest, terminal_id: required('string') },
  agent_terminal_release: { ...commonRequest, terminal_id: required('string') },
  agent_terminal_read_background_output: { ...commonRequest, path: required('string') },
  agent_terminal_kill_background: { ...commonRequest, path: required('string') },
  terminal_spawn: {
    ...commonWorkspace,
    ...commonRequest,
    terminal_id: required('string'),
    cols: optional('number'),
    rows: optional('number'),
  },
  terminal_write: { terminal_id: required('string'), data: required('string') },
  terminal_kill: { terminal_id: required('string') },
  terminal_resize: {
    terminal_id: required('string'),
    cols: optional('number'),
    rows: optional('number'),
  },
} as const satisfies Record<string, Record<string, FieldSpec>>;

export type WorkspaceRequestFrameType = keyof typeof requestSchemas;
export type ValidatedWorkspaceRequestFrame = Record<string, unknown> & {
  type: WorkspaceRequestFrameType;
  request_id?: string;
  workspace_root?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function matches(kind: FieldKind, value: unknown): boolean {
  if (kind === 'unknown') return true;
  if (kind === 'nullable-string') return value === null || typeof value === 'string';
  if (kind === 'nullable-number')
    return value === null || (typeof value === 'number' && Number.isFinite(value));
  if (kind === 'nullable-record') return value === null || isRecord(value);
  if (kind === 'record') return isRecord(value);
  if (kind === 'string[]')
    return Array.isArray(value) && value.every((item) => typeof item === 'string');
  if (kind === 'string-record')
    return isRecord(value) && Object.values(value).every((item) => typeof item === 'string');
  return typeof value === kind && (kind !== 'number' || Number.isFinite(value));
}

/** Returns null for frames outside the native workspace protocol and throws for malformed requests. */
export function parseWorkspaceRequestFrame(
  value: Record<string, unknown>,
): ValidatedWorkspaceRequestFrame | null {
  const type = value['type'];
  if (typeof type !== 'string' || !(type in requestSchemas)) return null;
  const schema = requestSchemas[type as WorkspaceRequestFrameType];
  for (const [field, spec] of Object.entries(schema)) {
    const fieldValue = value[field];
    if (fieldValue === undefined && spec.optional) continue;
    if (fieldValue === undefined) throw new Error(`Missing required field: ${field}.`);
    if (!matches(spec.kind, fieldValue))
      throw new Error(`Invalid ${field}: expected ${spec.kind}.`);
  }
  return value as ValidatedWorkspaceRequestFrame;
}
